import axios from 'axios';

import auth from '../libs/auth';
import { AxiosCache, AxiosCacheRequestConfig } from '../libs/AxiosCache';
import Material from './Material';
import Spec from './Spec';

export interface CourseWorkType {
  id: string;
  courseId: string;
  title: string;
  description: string;
  materials: Material[] | null;
  state: string;
  alternateLink: string;
  creationTime: string;
  updateTime: string;
  maxPoints: number;
  workType: string;
  associatedWithDeveloper: boolean;
  topicId: string;
  checked?: boolean;
  dueDate?: DueDateType;
  dueTime?: DueTimeType;

  //moodle assignment
  name: string;
  grade: number;
  cmid: number;
}

export interface DueDateType {
  day: number
  month: number
  year: number
}

export interface DueTimeType {
  hours: number
  minutes: number
}

class CourseWork implements CourseWorkType {
  id: string
  courseId: string
  title: string
  description: string
  materials: Material[] | null
  state: string
  alternateLink: string
  creationTime: string
  updateTime: string
  maxPoints: number
  workType: string
  associatedWithDeveloper: boolean
  topicId: string
  checked?: boolean
  dueDate?: DueDateType
  dueTime?: DueTimeType
  name: string;
  grade: number;
  cmid: number;

  constructor(courseWork: CourseWorkType) {
    this.id = courseWork.id;
    this.courseId = courseWork.courseId;
    this.title = courseWork.title;
    this.description = courseWork.description;
    this.materials = courseWork.materials ? courseWork.materials.map(a => new Material(a)) : null;
    this.state = courseWork.state;
    this.alternateLink = courseWork.alternateLink;
    this.creationTime = courseWork.creationTime;
    this.updateTime = courseWork.updateTime;
    this.maxPoints = courseWork.maxPoints;
    this.workType = courseWork.workType;
    this.associatedWithDeveloper = courseWork.associatedWithDeveloper;
    this.topicId = courseWork.topicId;
    this.dueDate = courseWork.dueDate;
    this.dueTime = courseWork.dueTime;
    this.name = courseWork.name;
    this.grade = courseWork.grade;
    this.cmid = courseWork.cmid;
  }

  // checks if the course work type is supported by the platform
  get isSupported(): boolean {

    // only assignment is supported by the platform
    if (this.workType === 'ASSIGNMENT') {

      // if a google form was detected it's a quiz assignment, not an actual assignment
      return !this.materials?.find(a => a.form);
    }

    return false;
  }

  async associate(): Promise<CourseWork> {
    const { data } = await axios.post('/classroom/courses/course-work/associate', {
      courseId: this.courseId,
      courseWorkId: this.id
    });
    AxiosCache.reset();
    return new CourseWork(data);
  }

  static async getMoodleCourseAssignments(query: { [key: string]: any } = {}, config?: AxiosCacheRequestConfig): Promise<CourseWork[]> {
    const session = auth.getSession();
    const { data = [] } = await axios.post('/student/course/get-assignments', {...query, token: session?.accessToken});

    return (data as CourseWorkType[]).map((item) => new CourseWork({...item, id: item.id.toString()}));
  }

  async getSpecs(): Promise<Spec[]> {
    return await Spec.find({
      assignmentID: this.id
    });
  }

  async setSpecs(specIds: string[]) {
    const { data } = await axios.put('/admin/assignment/specs', {
      assignmentId: this.id,
      specIds: specIds
    });
    return data;
  }

  async addSpec(specId: string) {
    return await CourseWork.addSpec(this.id, specId);
  }

  async removeSpec(specId: string) {
    return await CourseWork.removeSpec(this.id, specId);
  }

  static async addSpec(courseWorkId: string, specId: string) {
    const { data } = await axios.post('/admin/assignment/spec', {
      assignmentID: courseWorkId,
      specId
    });
    return data;
  }

  static async removeSpec(courseWorkId: string, specId: string) {
    const { data } = await axios.delete('/admin/assignment/spec', {
      data: {
        assignmentID: courseWorkId,
        specId
      }
    });
    return data;
  }

  static async cloneSpecs(sourceCourseWorkId: string, targetCourseWorkId: string) {
    const { data } = await axios.post('/admin/assignment/clone', {
      sourceCourseWorkId,
      targetCourseWorkId,
    });
    return data;
  }

  static async find(query?: {courseId: string}, config?: AxiosCacheRequestConfig): Promise<CourseWork[]> {
    const [{ data = [] }, { data: topics = [] }] = await Promise.all([
      axios.get('/classroom/courses/course-work/list', AxiosCache.getConfig({
        ...config, params: query
      })),
      axios.get('/classroom/courses/topics/list', AxiosCache.getConfig({
        ...config, params: query
      }))
    ]);

    const _data = [...data]; // remove array reference
    let sortedData: CourseWorkType[] = [];
    for (const topic of topics) {
      const items = _data.filter((a: CourseWorkType) => a.topicId === topic.topicId);
      for (let item of items) {
        sortedData = [...sortedData, ..._data.splice(_data.indexOf(item), 1)];
      }
    }
    sortedData = [...sortedData, ..._data].filter(courseWork => {
      const topic = topics.find((t: any) => t.topicId === courseWork.topicId);
      const topicName = topic?.name || '';

      // filter attendance course works
      return !/attendance/i.test(topicName);
    });
    return (sortedData).map(a => new CourseWork(a)).filter(a => a.isSupported);
  }

  static async getSubmissions(query: { [key: string]: any } = {}, config?: AxiosCacheRequestConfig): Promise<any> {
    const session = auth.getSession();

    const {data} = await axios.post('/student/course/get-assignment-submissions', {...query, token: session?.accessToken});
    
    // const _data = data as MoodleSubmissionData;
    // const studentSubmissions: MoodleSubmissionData = {
    //   ..._data,
    //   assignments: _data.assignments.map((obj) => ({
    //     ...obj,
    //     submissions: obj.submissions.filter(
    //       (submission) => submission.userid === session?.user.userid
    //     ),
    //   })),
    // };

    return data;
  }

  static async getSubmission(query: {assignmentIds: string[], userId?: number}, config?: AxiosCacheRequestConfig): Promise<MoodleSubmissionData | any> {
    const session = auth.getSession();

    const submission = await axios.post('/student/course/get-assignment-submission', {...query, token: session?.accessToken});

    return submission;
  }

  static async findOne(query?: {courseId: string, id: string}, config?: AxiosCacheRequestConfig): Promise<CourseWork> {
    const { data } = await axios.get('/classroom/courses/course-work', AxiosCache.getConfig({
      ...config, params: query
    }));

    const courseWork = new CourseWork(data);
    if (courseWork.isSupported)
      return courseWork;
    throw new Error('course work is not supported by the platform');
  }

  static getListUrlPath(courseId: string, role: string) {
    return `/${role}/courses/${courseId}/assignments`;
  }
}

export default CourseWork;


interface File {
  filename: string;
  filepath: string;
  filesize: number;
  fileurl: string;
  timemodified: number;
  mimetype: string;
  isexternalfile: boolean;
}

interface Plugin {
  type: string;
  name: string;
  fileareas: FileArea[] | null;
}

interface FileArea {
  area: string;
  files: File[];
}

interface Submission {
  id: number;
  userid: number;
  attemptnumber: number;
  timecreated: number;
  timemodified: number;
  timestarted: number;
  status: string;
  groupid: number;
  plugins: Plugin[];
  gradingstatus: string;
}

interface Assignment {
  assignmentid: number;
  submissions: Submission[];
}

interface MoodleSubmissionData {
  assignments: Assignment[];
  warnings: any[];
}